import { useState, useEffect } from 'react';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { BarChart3, Users, DollarSign, Target, TrendingUp, Award } from 'lucide-react';
import Navigation from '@/components/Navigation';

function Analytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await API.get('/admin/analytics');
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load analytics');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  const progressToGoal = ((stats?.monthly_revenue || 0) / 10000) * 100;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-12">
          <h1 className="font-['Outfit'] text-4xl font-bold mb-3" data-testid="analytics-title">Analytics Dashboard</h1>
          <p className="text-gray-400">Track your progress toward £10,000/month revenue goal</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="ninja-card" data-testid="stat-total-users">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-green-500 w-8 h-8" />
              <span className="text-3xl font-bold">{stats?.total_users || 0}</span>
            </div>
            <p className="text-gray-400 text-sm">Total Users</p>
            <p className="text-xs text-gray-500 mt-1">Free: {stats?.free_users || 0} | Premium: {stats?.premium_users || 0}</p>
          </div>

          <div className="ninja-card" data-testid="stat-conversion">
            <div className="flex items-center justify-between mb-2">
              <Target className="text-green-500 w-8 h-8" />
              <span className="text-3xl font-bold">{stats?.conversion_rate || 0}%</span>
            </div>
            <p className="text-gray-400 text-sm">Conversion Rate</p>
          </div>

          <div className="ninja-card" data-testid="stat-monthly-revenue">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-yellow-500 w-8 h-8" />
              <span className="text-3xl font-bold">£{stats?.monthly_revenue || 0}</span>
            </div>
            <p className="text-gray-400 text-sm">Monthly Revenue</p>
            <p className="text-xs text-gray-500 mt-1">From {stats?.paid_transactions || 0} transactions</p>
          </div>
        </div>

        <div className="ninja-card mb-8">
          <h2 className="font-['Outfit'] text-2xl font-bold mb-4">Progress to £10K/Month Goal</h2>
          <div className="relative">
            <div className="progress-bar h-6">
              <div 
                className="progress-indicator transition-all duration-500" 
                style={{ width: `${Math.min(progressToGoal, 100)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-400 mt-2">
              <span>£0</span>
              <span className="font-bold text-green-500">£{stats?.monthly_revenue || 0} ({progressToGoal.toFixed(1)}%)</span>
              <span>£10,000</span>
            </div>
          </div>
          <p className="text-gray-400 text-sm mt-4">
            Need {Math.ceil((10000 - (stats?.monthly_revenue || 0)) / 7.99)} more premium users to reach goal
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="ninja-card">
            <h3 className="font-['Outfit'] text-xl font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="text-green-500 w-6 h-6" />
              Engagement Metrics
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-white/10">
                <span className="text-gray-400">Total Roleplays</span>
                <span className="font-bold text-xl">{stats?.total_roleplays || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/10">
                <span className="text-gray-400">Completed Sessions</span>
                <span className="font-bold text-xl">{stats?.completed_roleplays || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/10">
                <span className="text-gray-400">Scripts Generated</span>
                <span className="font-bold text-xl">{stats?.total_scripts || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-400">Completion Rate</span>
                <span className="font-bold text-xl text-green-500">
                  {stats?.total_roleplays > 0 ? Math.round((stats.completed_roleplays / stats.total_roleplays) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>

          <div className="ninja-card">
            <h3 className="font-['Outfit'] text-xl font-bold mb-4 flex items-center gap-2">
              <Award className="text-yellow-500 w-6 h-6" />
              Community Impact
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-white/10">
                <span className="text-gray-400">Total Wins Logged</span>
                <span className="font-bold text-xl">{stats?.total_wins || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/10">
                <span className="text-gray-400">Total Savings</span>
                <span className="font-bold text-xl text-green-500">£{stats?.total_savings || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-400">Avg per Win</span>
                <span className="font-bold text-xl">£{stats?.avg_savings_per_win || 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 ninja-card bg-gradient-to-r from-green-500/10 to-transparent border-green-500/30 text-center">
          <h3 className="font-['Outfit'] text-xl font-bold mb-2">💡 Growth Tips</h3>
          <p className="text-gray-300 text-sm">
            {stats?.conversion_rate < 5 && "Focus on improving free-to-premium conversion with better onboarding and value demos."}
            {stats?.conversion_rate >= 5 && stats?.conversion_rate < 10 && "Good conversion rate! Consider adding referral program to boost user acquisition."}
            {stats?.conversion_rate >= 10 && "Excellent conversion! Scale up marketing to reach more users."}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Analytics;